import { CommonModule } from "@angular/common";
import { RouterModule } from "@angular/router";
import { Component, Input, Output, EventEmitter, HostBinding, HostListener } from "@angular/core";
import { IconComponent } from "../icon";

type ButtonVariant = "primary" | "secondary" | "outline" | "ghost" | "danger" | "success" | "link";
type ButtonSize = "sm" | "md" | "lg";

@Component({
  selector: "button[app-button], a[app-button]",
  standalone: true,
  imports: [CommonModule, IconComponent, RouterModule],
  template: `
    <app-icon
      *ngIf="icon && iconPosition === 'left'"
      [name]="icon"
      [className]="getIconSize()"
    ></app-icon>

    <ng-content *ngIf="!loading"></ng-content>

    <span *ngIf="loading" class="flex items-center gap-2">
      <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      {{ loadingText }}
    </span>

    <app-icon
      *ngIf="icon && iconPosition === 'right'"
      [name]="icon"
      [className]="getIconSize()"
    ></app-icon>
  `,
})
export class ButtonComponent {
  @Input() type: "button" | "submit" | "reset" = "button";
  @Input() variant: ButtonVariant = "primary";
  @Input() size?: ButtonSize;
  @Input() disabled: boolean = false;
  @Input() loading: boolean = false;
  @Input() loadingText: string = "Carregando...";
  @Input() icon?: string;
  @Input() iconPosition: "left" | "right" = "left";
  @Input() className: string = "";
  @Input() routerLink?: string | any[];
  @Input() queryParams?: { [key: string]: any };

  @Output() clicked = new EventEmitter<Event>();

  @HostBinding('class') get hostClasses(): string {
    return this.getButtonClasses();
  }

  @HostBinding('attr.type') get buttonType(): string | null {
    return this.routerLink ? null : this.type;
  }

  @HostBinding('attr.disabled') get isDisabled(): boolean | null {
    return this.routerLink ? null : (this.disabled || this.loading || null);
  }

  @HostBinding('attr.aria-disabled') get ariaDisabled(): string | null {
    return (this.disabled || this.loading) ? 'true' : null;
  }

  @HostBinding('attr.tabindex') get tabIndex(): number | null {
    return (this.disabled || this.loading) ? -1 : null;
  }

  @HostListener('click', ['$event'])
  handleClick(event: Event): void {
    if (!this.disabled && !this.loading) {
      this.clicked.emit(event);
    }
  }

  getButtonClasses(): string {
    const baseClasses =
      "inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-offset-2";

    const variantClasses = {
      primary: "bg-[#4A90E2] text-white hover:bg-[#2c5f8d] focus:ring-[#4A90E2]",
      secondary: "bg-[#F58242] text-white hover:bg-[#d46b1a] focus:ring-[#F58242]",
      outline:
        "border-2 border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-500",
      ghost: "text-gray-700 hover:bg-gray-100 focus:ring-gray-500",
      danger: "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500",
      success: "bg-green-600 text-white hover:bg-green-700 focus:ring-green-500",
      link: "text-[#4A90E2] hover:underline focus:underline",
    };

    const sizeClasses = {
      sm: "px-3 py-1.5 text-sm",
      md: "px-4 py-2 text-base",
      lg: "px-6 py-3 text-lg",
    };

    const disabledClasses =
      this.disabled || this.loading ? "opacity-50 cursor-not-allowed" : "";

    // Se size não for passado, o botão preenche todo o espaço (w-full)
    const widthClass = this.size ? "" : "w-full";
    const sizeClass = this.size ? sizeClasses[this.size] : "px-4 py-2 text-base";

    return `${baseClasses} ${variantClasses[this.variant]} ${sizeClass} ${disabledClasses} ${widthClass} ${this.className}`;
  }

  getIconSize(): string {
    const sizes = {
      sm: "w-3 h-3",
      md: "w-4 h-4",
      lg: "w-5 h-5",
    };
    return this.size ? sizes[this.size] : "w-4 h-4";
  }
}
